/* eslint-disable max-lines-per-function */

import { useAnlageassistentFormContext } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentFormContext';
import { useAnlageassistentContext } from '@/domain/mitarbeiter/features/anlageassistent/context/useAnlageassistentContext';
import { useArbeitsbereiche } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useArbeitsbereiche/useArbeitsbereiche';
import { useErfahrungslevelOptions } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useErfahrungslevelOptions';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { Badge, Body, Button, DataDisplayPlain, Title } from '@e2n/ui/v2';
import { faEllipsis, faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_SICHTBARE_SEKUNDAERE_ARBEITSBEREICHE = 3;

// oxlint-disable-next-line complexity
export function Arbeitsbereiche() {
  const { t } = useTranslation();
  const { ctx } = useAnlageassistentContext();
  const form = useAnlageassistentFormContext();
  const { currentStandort } = useStandortContext();
  const { arbeitsbereiche } = useArbeitsbereiche(currentStandort.id);

  const data = form.state.values;

  const primaererArbeitsbereich = arbeitsbereiche.find(
    (ab) => ab.id === data.person.arbeitsbereiche.primaererArbeitsbereich,
  );

  const sekundaereArbeitsbereiche = arbeitsbereiche.filter((ab) =>
    data.person.arbeitsbereiche.sekundaereArbeitsbereiche.includes(ab.id),
  );
  const [alleSekundaerenAnzeigen, setAlleSekundaerenAnzeigen] = useState(false);
  const sichtbareSekundaereArbeitsbereiche = alleSekundaerenAnzeigen
    ? sekundaereArbeitsbereiche
    : sekundaereArbeitsbereiche.slice(
        0,
        MAX_SICHTBARE_SEKUNDAERE_ARBEITSBEREICHE,
      );
  const erfahrungslevelOptions = useErfahrungslevelOptions();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 items-center justify-between">
        <Title className="wrap-break-word">
          {t('domain.mitarbeiter.create.steps.person.arbeitsbereiche.title')}
        </Title>
        <Button
          iconOnly
          variant={'secondary-red-on-grey'}
          className="bg-neutrals-v2-100"
          onClick={() => ctx.navigateToSubStep('person.arbeitsbereiche')}>
          <FontAwesomeIcon icon={faPen} />
        </Button>
      </div>
      <div className="flex flex-col gap-3">
        <DataDisplayPlain
          orientation="vertical"
          label={t(
            'domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.primaererArbeitsbereich.label',
          )}
          item={
            primaererArbeitsbereich ? (
              <Badge
                size="md"
                layout="character-dot"
                dotColor={primaererArbeitsbereich.getBorderColor()}
                className="bg-neutrals-v2-100">
                {primaererArbeitsbereich.name}
              </Badge>
            ) : (
              <Body weight="semibold">{'-'}</Body>
            )
          }
        />
        <DataDisplayPlain
          orientation="vertical"
          label={t(
            'domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.erfahrungslevel.label',
          )}
          item={
            data.person.arbeitsbereiche.erfahrungslevel ? (
              <Badge
                size="md"
                layout="character"
                className="bg-neutrals-v2-100"
                icon={
                  <FontAwesomeIcon
                    icon={
                      erfahrungslevelOptions.find(
                        (o) =>
                          o.key === data.person.arbeitsbereiche.erfahrungslevel,
                      )?.icon || faEllipsis
                    }
                  />
                }>
                {data.person.arbeitsbereiche.erfahrungslevel}
              </Badge>
            ) : (
              <Body weight="semibold">{'-'}</Body>
            )
          }
        />

        {currentStandort.qualifikationenEnabled && (
          <div className="flex flex-col items-start gap-2">
            <DataDisplayPlain
              orientation="vertical"
              label={t(
                'domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.sekundaereArbeitsbereiche.label',
              )}
              item={
                sekundaereArbeitsbereiche.length > 0 ? (
                  <span className="flex flex-wrap gap-2">
                    {sichtbareSekundaereArbeitsbereiche.map((ab) => (
                      <Badge
                        key={ab.id}
                        size="md"
                        layout="character-dot"
                        dotColor={ab.getBorderColor()}
                        className="bg-neutrals-v2-100">
                        {ab.name}
                      </Badge>
                    ))}
                  </span>
                ) : (
                  <Body weight="semibold">{'-'}</Body>
                )
              }
            />
            {!alleSekundaerenAnzeigen &&
              sekundaereArbeitsbereiche.length >
                MAX_SICHTBARE_SEKUNDAERE_ARBEITSBEREICHE && (
                <Button
                  variant="ghost-on-grey"
                  onClick={() => setAlleSekundaerenAnzeigen(true)}>
                  {t(
                    'domain.mitarbeiter.create.steps.person.arbeitsbereiche.form.sekundaereArbeitsbereiche.alleAnzeigen',
                  )}
                </Button>
              )}
          </div>
        )}
      </div>
    </div>
  );
}
